/**
 * Texto da Política de Privacidade (RASCUNHO para revisão jurídica)
 *
 * Escrito a partir do que o sistema realmente coleta e faz (levantamento no
 * código em 27/09/2026). Ao mudar o sistema (novo dado, novo fornecedor,
 * rotina de exclusão), atualize este texto e a data em company.ts.
 *
 * Usa os mesmos blocos dos artigos do blog: **negrito** e [links](/caminho).
 *
 * @author Equipe VendeAi
 * @since 1.4.0
 */

import type { BlogBlock } from '../blog/types';
import { BRAND } from '../content';
import { LEGAL_COMPANY } from './company';

const fill = (value: string) => value.trim() || '[a preencher]';

export const PRIVACY_TITLE = `Política de Privacidade | ${BRAND}`;
export const PRIVACY_DESCRIPTION =
  'Como o Vende Aí coleta, usa, compartilha e protege dados pessoais de visitantes do site, estabelecimentos clientes e dos clientes deles, e como exercer seus direitos pela LGPD.';

export function privacyPolicyBlocks(): BlogBlock[] {
  const c = LEGAL_COMPANY;
  return [
    {
      type: 'p',
      text: `Esta política explica como o ${BRAND} trata dados pessoais, em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018, **LGPD**). Ela vale para o site vendeai.net.br, para o sistema usado pelos estabelecimentos (painel, cardápio digital, pedidos por mesa e robô de WhatsApp) e para o atendimento comercial.`,
    },

    { type: 'h2', text: '1. Quem somos' },
    {
      type: 'ul',
      items: [
        `**Empresa:** ${fill(c.legalName)}`,
        `**CNPJ:** ${fill(c.document)}`,
        `**Endereço:** ${fill(c.address)}`,
        `**Encarregado pelo tratamento de dados (DPO):** ${fill(c.dpoName)}`,
        `**Contato para assuntos de privacidade:** ${fill(c.dpoEmail)}`,
      ],
    },

    { type: 'h2', text: '2. Nosso papel: controlador e operador' },
    {
      type: 'p',
      text: `Pela LGPD, quem decide como e por que os dados são usados é o **controlador**; quem trata os dados em nome dele é o **operador**.`,
    },
    {
      type: 'table',
      head: ['Dados', `Papel do ${BRAND}`, 'Quem decide'],
      rows: [
        [
          'Visitantes do site, contatos comerciais (leads) e contas dos estabelecimentos',
          'Controlador',
          BRAND,
        ],
        [
          'Clientes dos estabelecimentos, pedidos, conversas de WhatsApp, entregadores e funcionários cadastrados pelo estabelecimento',
          'Operador',
          'O estabelecimento (restaurante, loja, barbearia...)',
        ],
      ],
    },
    {
      type: 'tip',
      title: 'Cliente de um estabelecimento?',
      text: `Se você fez um pedido numa loja que usa o ${BRAND}, o responsável pelos seus dados é essa loja. Fale primeiro com ela. Se precisar, também atendemos pelo contato da seção 1 e encaminhamos o pedido ao estabelecimento.`,
    },

    { type: 'h2', text: '3. Quais dados tratamos e para quê' },
    { type: 'h3', text: '3.1 Visitantes do site' },
    {
      type: 'ul',
      items: [
        '**Formulário de contato:** nome, e-mail, telefone, nome da empresa, segmento, plano de interesse e mensagem, além do endereço IP do envio (para evitar abuso). Finalidade: responder ao contato e apresentar o serviço. Base legal: procedimentos preliminares a contrato, a seu pedido, e legítimo interesse.',
        '**Estatísticas de acesso (Google Analytics):** só com o seu consentimento no aviso de cookies. Coleta páginas visitadas, origem da visita, tipo de aparelho e navegador, com identificadores de cookie. Finalidade: entender o uso do site e melhorá-lo. Base legal: consentimento, que pode ser retirado a qualquer momento em "Preferências de cookies", no rodapé.',
        '**Fontes do site:** as fontes são carregadas do Google Fonts, que recebe o endereço IP do seu navegador para entregar os arquivos.',
      ],
    },
    { type: 'h3', text: '3.2 Estabelecimentos clientes e seus usuários' },
    {
      type: 'ul',
      items: [
        '**Cadastro e conta:** nome do responsável, e-mail, telefone/WhatsApp, senha (guardada só em forma criptografada, com hash), nome, CNPJ ou CPF, endereço e dados de contato do estabelecimento, e o registro de aceite dos termos. Finalidade: criar e manter a conta e prestar o serviço. Base legal: execução de contrato.',
        '**Acesso e segurança:** data do último acesso, endereço IP e navegador das sessões e registros de auditoria das ações feitas no painel. Finalidade: segurança, prevenção a fraudes e cumprimento do Marco Civil da Internet. Base legal: obrigação legal e legítimo interesse.',
        '**Assinatura:** nome, e-mail, CPF ou CNPJ e telefone do pagador, enviados ao gateway de cobrança da mensalidade. Finalidade: cobrar o plano. Base legal: execução de contrato.',
        '**Configurações de pagamento e fiscais:** credenciais dos gateways escolhidos pelo estabelecimento (guardadas criptografadas) e o certificado digital A1 para emissão de notas. Finalidade: receber pagamentos e emitir documentos fiscais em nome do estabelecimento.',
      ],
    },
    { type: 'h3', text: '3.3 Dados tratados em nome dos estabelecimentos (como operador)' },
    {
      type: 'ul',
      items: [
        '**Clientes das lojas:** nome, telefone, e-mail, CPF (quando informado, por exemplo para nota fiscal), data de aniversário, endereços de entrega, histórico de pedidos, pontos e cashback de fidelidade, preferências de pagamento e de notificação.',
        '**Pedidos e pagamentos:** itens, valores, observações, forma de pagamento e dados de retorno do gateway (identificador da transação, bandeira e últimos 4 dígitos do cartão). **O número completo do cartão e o código de segurança não são gravados por nós.** Na maioria dos gateways, o cartão é digitado em um componente do próprio gateway, que devolve apenas um código (token). Nos provedores que não oferecem esse recurso (Stone Online, Rede, Getnet e Asaas), o cartão passa pelo nosso servidor apenas em memória, durante a transação, e esse modo só é ativado após certificação PCI DSS.',
        '**WhatsApp:** mensagens trocadas com o número do estabelecimento (inclusive fotos, áudios, vídeos e documentos enviados pelo cliente, guardados em área restrita à equipe de atendimento), nome de perfil e telefone, para o robô de atendimento montar pedidos e para o histórico de atendimento. A conexão é feita pelo WhatsApp Web do número do estabelecimento; as mensagens também trafegam pelos servidores do WhatsApp (Meta).',
        '**Carrinhos abandonados e campanhas:** itens deixados no carrinho e contatos, para lembretes e campanhas configurados pelo estabelecimento.',
        '**Entregadores:** nome, telefone, e-mail, CPF, veículo e placa, e a localização durante as entregas.',
        '**Funcionários (módulo de RH):** dados cadastrais e trabalhistas (incluindo CPF, salário, endereço e contato de emergência), registros de ponto com localização, foto e aparelho, e atestados médicos. Atestados podem conter **dados de saúde**, que são dados sensíveis: o estabelecimento deve tratá-los apenas nas hipóteses do art. 11 da LGPD e restringir o acesso a quem precisa.',
        '**Notas fiscais:** nome, CPF ou CNPJ e e-mail do destinatário, enviados à Secretaria da Fazenda (SEFAZ) do estado.',
      ],
    },

    { type: 'h2', text: '4. Com quem compartilhamos' },
    {
      type: 'p',
      text: 'Não vendemos dados pessoais. Compartilhamos apenas o necessário para o serviço funcionar:',
    },
    {
      type: 'table',
      head: ['Quem', 'Para quê'],
      rows: [
        [
          `Hospedagem (${fill(c.hosting)})`,
          'Servidores onde o sistema e o banco de dados funcionam',
        ],
        [
          'Gateways de pagamento e bancos escolhidos pelo estabelecimento (ex.: Mercado Pago, PagBank, Asaas, Stripe, bancos com API Pix)',
          'Processar PIX, cartão e maquininhas. Cada gateway tem sua própria política de privacidade',
        ],
        ['Gateway de cobrança da mensalidade', 'Cobrar a assinatura dos estabelecimentos'],
        ['WhatsApp (Meta)', 'Troca de mensagens com clientes pelo número do estabelecimento'],
        ['Provedor de e-mail (SMTP)', 'Enviar e-mails de conta, senha e avisos'],
        ['Secretarias da Fazenda (SEFAZ)', 'Autorizar notas fiscais (obrigação legal)'],
        ['ViaCEP', 'Preencher o endereço a partir do CEP digitado'],
        [
          'Serviços de notificação dos navegadores',
          'Entregar notificações push autorizadas pelo usuário',
        ],
        [
          'Google (Analytics e Fonts)',
          'Estatísticas de acesso, só com consentimento, e fontes do site',
        ],
        ['Autoridades', 'Quando exigido por lei ou ordem judicial'],
      ],
    },
    {
      type: 'p',
      text: 'Alguns desses fornecedores (por exemplo Google e parte dos gateways de pagamento) podem armazenar dados fora do Brasil. Nesses casos, a transferência ocorre para a execução do contrato ou com as garantias previstas no art. 33 da LGPD.',
    },

    { type: 'h2', text: '5. Cookies e armazenamento no navegador' },
    {
      type: 'ul',
      items: [
        '**Necessários (sempre ativos):** manter o login no painel, o carrinho e a sessão da mesa, preferências de tema e de menu, e a sua escolha sobre cookies. Ficam no armazenamento local do navegador e não são usados para publicidade.',
        '**Estatísticas (opcionais):** cookies do Google Analytics, só depois que você clicar em "Aceitar". Você pode mudar a escolha em "Preferências de cookies", no rodapé do site.',
      ],
    },

    { type: 'h2', text: '6. Por quanto tempo guardamos' },
    {
      type: 'p',
      text: 'Uma rotina diária apaga ou anonimiza os dados que passaram do prazo. Nos dados tratados em nome dos estabelecimentos, cada estabelecimento pode ajustar os prazos na tela LGPD do sistema, respeitando os mínimos legais. Os prazos padrão são:',
    },
    {
      type: 'table',
      head: ['Dado', 'Prazo padrão', 'O que acontece'],
      rows: [
        ['Mensagens do WhatsApp', '1 ano', 'Apagadas (o conteúdo técnico bruto, após 90 dias)'],
        [
          'Fotos, áudios, vídeos e documentos recebidos no WhatsApp',
          '90 dias',
          'Arquivos apagados (a mensagem fica no histórico)',
        ],
        [
          'Registros de auditoria e de acesso',
          '1 ano (mínimo de 6 meses, Marco Civil da Internet)',
          'Apagados',
        ],
        ['Localização das entregas', '90 dias', 'Apagada'],
        ['Carrinhos abandonados', '90 dias', 'Apagados'],
        ['Contatos do WhatsApp que não viraram clientes', '2 anos sem interação', 'Apagados'],
        [
          'Foto, localização e aparelho do ponto eletrônico',
          '5 anos (mínimo legal)',
          'Apagados; o horário da batida é mantido',
        ],
        [
          'Detalhes de atestados médicos (CID, médico, anexos)',
          '5 anos (mínimo legal)',
          'Apagados; o período de afastamento é mantido',
        ],
        ['Contatos comerciais do site (leads)', '2 anos sem movimentação', 'Apagados'],
        [
          'Pedidos, pagamentos e notas fiscais',
          'Prazo da legislação fiscal (em regra, 5 anos)',
          'Mantidos pelo prazo legal',
        ],
        [
          'Conta do estabelecimento e cadastro de clientes',
          'Enquanto o contrato estiver ativo',
          'Excluídos ou anonimizados a pedido, ou após o encerramento e os prazos legais',
        ],
        ['Estatísticas do Google Analytics', 'Até 14 meses', 'Conforme configuração da ferramenta'],
      ],
    },

    { type: 'h2', text: '7. Como protegemos os dados' },
    {
      type: 'ul',
      items: [
        'Conexão criptografada (HTTPS) entre o navegador e o sistema.',
        'Senhas guardadas apenas com hash (bcrypt) e credenciais de pagamento criptografadas (AES-256-GCM).',
        'Dados de cartão nunca gravados em banco, fila ou log; na maioria dos gateways, digitados direto no componente do gateway (tokenização).',
        'Cada estabelecimento acessa apenas os próprios dados, com perfis de acesso por usuário e registro de auditoria.',
        'Em caso de incidente de segurança com risco relevante, comunicaremos os afetados e a ANPD, como prevê o art. 48 da LGPD.',
      ],
    },

    { type: 'h2', text: '8. Seus direitos' },
    {
      type: 'p',
      text: 'Pelo art. 18 da LGPD, você pode pedir, a qualquer momento:',
    },
    {
      type: 'ul',
      items: [
        'confirmação de que tratamos seus dados e acesso a eles;',
        'correção de dados incompletos, inexatos ou desatualizados;',
        'anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desacordo com a lei;',
        'portabilidade dos dados;',
        'eliminação dos dados tratados com base no consentimento e informação sobre a possibilidade de não consentir;',
        'informação sobre com quem compartilhamos seus dados;',
        'revogação do consentimento.',
      ],
    },
    {
      type: 'p',
      text: `**Como pedir:** envie um e-mail para ${fill(c.dpoEmail)}. Usuários do painel também podem ver, exportar, corrigir e excluir os próprios dados na tela **LGPD** do sistema. Respondemos em até 15 dias. Você também pode apresentar reclamação à Autoridade Nacional de Proteção de Dados (ANPD).`,
    },

    { type: 'h2', text: '9. Crianças e adolescentes' },
    {
      type: 'p',
      text: `O cadastro de estabelecimentos no ${BRAND} é destinado a maiores de 18 anos. O site e o sistema não são direcionados a crianças.`,
    },

    { type: 'h2', text: '10. Alterações desta política' },
    {
      type: 'p',
      text: `Podemos atualizar esta política quando o serviço mudar. A data da versão em vigor fica no topo da página e, em mudanças relevantes, avisaremos os estabelecimentos pelo painel ou por e-mail. **Versão em vigor desde:** ${fill(c.effectiveDate)}.`,
    },
  ];
}
